import { Radio, Tooltip, Typography } from '@material-tailwind/react'
import capitalize from 'lodash/capitalize'
import React from 'react'
import { PiInfoLight } from 'react-icons/pi'
import { useTranslation } from 'react-i18next'
import { AgamiUploadFormData } from './UploadAgamiInterface'

interface AgamiSpot {
  type: number
}

interface AgamiTypeSelectorProps {
  isPublic: boolean
  formData: AgamiSpot
  handleDataChange: <K extends keyof AgamiUploadFormData>(key: K, newValue: AgamiUploadFormData[K]) => void
  availableSpotTypes: Record<number, string>
  /**
   * Display overrides keyed by type id (06.3): the v2 shows `custom` as
   * "Defined by the application" — what WILL happen, not what is missing —
   * while the testids keep deriving from the raw label. Absent in the v1,
   * which renders exactly as it always has.
   */
  displayLabels?: Record<number, string>
}

const AgamiTypeSelector: React.FC<AgamiTypeSelectorProps> = ({
  isPublic,
  formData,
  handleDataChange,
  availableSpotTypes,
  displayLabels,
}) => {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-2 items-start bg-white dark:bg-primary rounded-lg p-4 shadow-sm">
      <div className="flex gap-1 justify-start items-center">
        <Typography className="font-bold text-primary text-lg font-barlow" data-testid="agami-type-title">
          {t('upload.type.title')}
        </Typography>
        <Tooltip
          content={
            <div className="w-[300px]">
              <Typography variant="small" className="font-bold">
                {t('upload.type.description')}
              </Typography>
            </div>
          }
          placement="right"
          interactive
        >
          <span>
            <PiInfoLight className="text-primary dark:text-base-gray-400" />
          </span>
        </Tooltip>
      </div>
      <div className="flex gap-1 justify-center items-center">
        {Object.keys(availableSpotTypes).map((k) => {
          const type = Number(k)
          return (
            <Radio
              key={k}
              type="radio"
              value={type}
              checked={formData.type === type}
              onChange={() => handleDataChange('type', type)}
              label={displayLabels?.[type] ?? capitalize(availableSpotTypes[type])}
              disabled={isPublic}
              data-testid={`agami-type-radio-${availableSpotTypes[type]}`}
              labelProps={{
                className: 'text-gray-600 dark:!text-base-gray-500',
              }}
            />
          )
        })}
      </div>
    </div>
  )
}

export default AgamiTypeSelector
